/**
 * NetobsLatencyHeatmapPanel (NO-7) — cross-player latency matrix.
 *
 * Rows: each connected player.
 * Columns: P1 (input), P2 (predict+render), RTT.
 * Cell color: ms vs phase budget (green/yellow/red/black per netobsHealthColor).
 *
 * Substrate:
 *  - Server runs LatencyAggregator (server/diagnostics/LatencyAggregator.ts),
 *    receives `clientPhaseSummary` from each client every ~5s, broadcasts
 *    `netobs:latency_aggregate` every ~5s with the cross-player snapshot.
 *  - This panel reads that via useNetobsLatencyAggregate.
 *
 * Lives in-game (alongside CombatLogPanel + AbilityLifecycleMonitor) —
 * same ToolWindow shape as NetobsPacketLogPanel for the same reason
 * (/diagnostic is a separate document; can't see live state).
 *
 * Empty state: shows "Waiting for first aggregate broadcast" until the
 * server sends one. Stale guard: if the most-recent broadcast is older
 * than 12s, the freshness label flips red.
 */

import React from 'react';
import { netobsHealthColor, NETOBS_COLORS } from '../netobs/colors';
import { useNetobsLatencyAggregate } from '../netobs/useNetobsLatencyAggregate';

// Per-phase budgets (ms) — match LatencyTab/LatencyOverlay conventions
// (PHASE_BUDGETS_MS in DiagnosticHub/tabs/types.ts).
const BUDGETS = {
  phase1: 4,    // input → command
  phase2: 8,    // predict + render
  rtt: 120,     // arbitrary "good RTT" ceiling — anything below 120ms is fine
};

const STALE_AFTER_MS = 12_000;

const tableStyle: React.CSSProperties = {
  width: '100%',
  fontFamily: 'monospace',
  fontSize: 11,
  borderCollapse: 'collapse',
};

const thStyle: React.CSSProperties = {
  textAlign: 'right',
  padding: '4px 8px',
  borderBottom: '1px solid #2a2f3a',
  color: '#7af',
  background: '#0d0f15',
};

const thNameStyle: React.CSSProperties = {
  ...thStyle,
  textAlign: 'left',
};

const tdStyle: React.CSSProperties = {
  padding: '4px 8px',
  borderBottom: '1px solid #1a1d24',
  textAlign: 'right',
  fontVariantNumeric: 'tabular-nums',
};

const tdNameStyle: React.CSSProperties = {
  ...tdStyle,
  textAlign: 'left',
  color: '#ddd',
};

function fmtMs(v: number): string {
  if (v < 1) return v.toFixed(2);
  if (v < 10) return v.toFixed(1);
  return Math.round(v).toString();
}

function CellMs({ value, budget }: { value: number; budget: number }): React.ReactElement {
  const color = netobsHealthColor(value, budget);
  return (
    <td style={{ ...tdStyle, color, fontWeight: value > budget ? 700 : 400 }}>
      {fmtMs(value)}
    </td>
  );
}

// Phase 3: folded into the unified window engine — CONTENT only. WindowHost
// owns the ToolWindow chrome + open/close (frozen geometry key
// `toolwindow_netobs-latency-heatmap`).
export const NetobsLatencyHeatmapPanelContent: React.FC = () => {
  const aggregate = useNetobsLatencyAggregate();

  const now = Date.now();
  const stale = aggregate ? (now - aggregate.receivedAt > STALE_AFTER_MS) : false;
  const ageS = aggregate ? Math.max(0, Math.round((now - aggregate.receivedAt) / 1000)) : 0;

  // Sort by RTT descending so the laggiest players are at the top.
  const rows = aggregate
    ? [...aggregate.snaps].sort((a, b) => (b.rttMs || 0) - (a.rttMs || 0))
    : [];

  return (
      <div data-testid="netobs-latency-heatmap-panel" style={{ padding: 8 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 8, fontSize: 11, color: '#aaa' }}>
          <span>
            {aggregate
              ? <>{rows.length} player{rows.length === 1 ? '' : 's'}</>
              : 'no data yet'}
          </span>
          {aggregate && (
            <span style={{ color: stale ? NETOBS_COLORS.HEALTH_BAD : '#666' }}>
              {stale ? `stale (${ageS}s old)` : `${ageS}s ago`}
            </span>
          )}
          <span style={{ marginLeft: 'auto', color: '#666', fontSize: 10 }}>
            broadcast every ~5s
          </span>
        </div>

        {!aggregate && (
          <div style={{ color: '#888', padding: 12, fontStyle: 'italic', fontSize: 11 }}>
            Waiting for first aggregate broadcast (server emits every ~5s once
            any client has reported phase data).
          </div>
        )}

        {aggregate && rows.length === 0 && (
          <div style={{ color: '#888', padding: 12, fontStyle: 'italic', fontSize: 11 }}>
            No fresh client samples (aggregator filters samples older than 12s).
          </div>
        )}

        {rows.length > 0 && (
          <div style={{
            border: '1px solid #2a2f3a',
            borderRadius: 3,
            background: '#0d0f15',
            maxHeight: 200,
            overflowY: 'auto',
          }}>
            <table style={tableStyle}>
              <thead>
                <tr>
                  <th style={thNameStyle}>player</th>
                  <th style={thStyle} title={`P1 — input → command (budget ${BUDGETS.phase1}ms)`}>P1</th>
                  <th style={thStyle} title={`P2 — predict + render (budget ${BUDGETS.phase2}ms)`}>P2</th>
                  <th style={thStyle} title={`RTT (good ≤ ${BUDGETS.rtt}ms)`}>RTT</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((snap) => (
                  <tr key={snap.sid}>
                    <td style={tdNameStyle}>
                      {snap.name || snap.sid}
                      {!snap.clockReady && (
                        <span style={{ color: '#777', marginLeft: 6, fontSize: 9 }}>(no sync)</span>
                      )}
                    </td>
                    <CellMs value={snap.phase1Ms} budget={BUDGETS.phase1} />
                    <CellMs value={snap.phase2Ms} budget={BUDGETS.phase2} />
                    <CellMs value={snap.rttMs} budget={BUDGETS.rtt} />
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        <div style={{ marginTop: 6, fontSize: 9, color: '#555', fontStyle: 'italic' }}>
          Color: <span style={{ color: NETOBS_COLORS.HEALTH_OK }}>green</span>=&lt;50% budget,{' '}
          <span style={{ color: NETOBS_COLORS.HEALTH_WARN }}>yellow</span>=50-90%,{' '}
          <span style={{ color: NETOBS_COLORS.HEALTH_BAD }}>red</span>=over budget.
        </div>
      </div>
  );
};
